import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { SearchField } from '@/components/ui/SearchField';
import { useHeaderSearchMode } from '@/contexts/HeaderSearchContext';
import type { HeaderSearchConfig } from '@/contexts/PageConfigContext';
import type { Tournament } from '@/types';
import { useSearchQueryParam } from '@/hooks/useSearchQueryParam';
import { usePrefetchTournamentSearchIndex } from '@/hooks/useTournamentSearchIndex';
import { SWIPE_BACK_EDGE_WIDTH } from '@/hooks/useSwipeBack';
import { OverlayPanel } from './OverlayPanel';
import styles from './styles.module.scss';

interface HeaderSearchTriggerProps {
  config: HeaderSearchConfig;
}

/**
 * Idle header search: the magnifier, or — while the URL has `q` — the active
 * query chip (tap reopens the search with it, ✕ resets it). Opening focuses the
 * field inside the tap (iOS opens the keyboard only then); the press already
 * starts loading the tab index for the panel.
 */
export const HeaderSearchTrigger: React.FC<HeaderSearchTriggerProps> = ({ config }) => {
  const { t } = useTranslation('common');
  const { open, triggerRef } = useHeaderSearchMode();
  const [q, setQ] = useSearchQueryParam();
  const prefetchIndex = usePrefetchTournamentSearchIndex();

  if (!q) {
    return (
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onPointerDown={prefetchIndex}
        onClick={open}
        aria-label={t('headerSearch.openWith', { value: config.placeholder })}
        data-testid="header-search-open"
      >
        <i className="icon-search" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div className={styles.chip}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.chipLabel}
        onPointerDown={prefetchIndex}
        onClick={open}
        aria-label={t('headerSearch.openWith', { value: q })}
        data-testid="header-search-chip"
      >
        <i className={`icon-search ${styles.chipIcon}`} aria-hidden="true" />
        <span className={styles.chipText}>{q}</span>
      </button>
      <button
        type="button"
        className={styles.chipReset}
        onClick={() => setQ('')}
        aria-label={t('headerSearch.reset')}
        data-testid="header-search-chip-reset"
      >
        <i className="icon-xmark" aria-hidden="true" />
      </button>
    </div>
  );
};

interface HeaderSearchBarProps {
  config: HeaderSearchConfig;
  /** The header — the suggestions panel draws below it and within its width. */
  anchorRef: RefObject<HTMLElement>;
}

/**
 * Header in search mode: back arrow + the stretched search field, with the
 * suggestions panel under the header. Submit (Enter / keyboard "Search" /
 * "Show all results") writes the trimmed text to URL `q` — the tab list below
 * filters by it — and leaves search mode. A suggestion tap writes it too, so
 * returning to the list keeps the query. Back, Escape and a tap outside leave
 * search mode with `q` as it is (edge-swipe, browser / hardware back: the
 * shell and `HeaderSearchContext`).
 */
export const HeaderSearchBar: React.FC<HeaderSearchBarProps> = ({ config, anchorRef }) => {
  const { t } = useTranslation('common');
  const { close, draftRef, inputRef } = useHeaderSearchMode();
  const [draft, setDraftState] = useState(() => draftRef.current);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const setDraft = (value: string) => {
    draftRef.current = value;
    setDraftState(value);
  };

  // A remount while open (lg breakpoint flip) drops the focus to <body> —
  // take it back. On open the field gets it from `open` anyway.
  useLayoutEffect(() => {
    if (document.activeElement === document.body) inputRef.current?.focus({ preventScroll: true });
  }, [inputRef]);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // A touch in the back-swipe edge zone may be the swipe, which exits itself.
      if (e.pointerType === 'touch' && e.clientX <= SWIPE_BACK_EDGE_WIDTH) return;
      close({ afterTap: true });
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [close]);

  const fill = (value: string) => {
    setDraft(value);
    inputRef.current?.focus();
  };

  const submit = () => {
    inputRef.current?.blur();
    close({ query: draft });
  };

  const select = (tournament: Tournament) => close({ query: draft, to: `/tournaments/${tournament.id}` });

  const exit = () => close({ returnFocus: true });

  // Dragging the results list hides the keyboard; the focus goes nowhere else.
  const hideKeyboard = () => {
    if (document.activeElement === inputRef.current) inputRef.current?.blur();
  };

  // Bubbles from the field and from the portaled panel (React tree).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    exit();
  };

  return (
    <div ref={rootRef} className={styles.bar} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className={styles.back}
        onClick={exit}
        aria-label={t('headerSearch.close')}
        data-testid="header-search-back"
      >
        <ArrowBackIosNewIcon />
      </button>
      <SearchField
        ref={inputRef}
        value={draft}
        onChange={setDraft}
        placeholder={config.placeholder}
        variant="pill"
        density="compact"
        onSubmit={submit}
        testId="header-search-input"
      />
      <OverlayPanel
        anchorRef={anchorRef}
        panelRef={panelRef}
        draft={draft}
        fill={fill}
        submit={submit}
        onSelect={select}
        onDrag={hideKeyboard}
      />
    </div>
  );
};
